/* eslint-disable max-lines */
import { IFilterValue } from 'models/request';
import { IFilterDinamicForm, IOption } from 'models/response';
import utils from 'utils';

export const validateFilter = (
  filter: IFilterDinamicForm,
  valore: any,
  filterErrors: { [key: string]: string },
  filters: IFilterDinamicForm[],
  filterValues: IFilterValue[],
  setFilterErrors: React.Dispatch<React.SetStateAction<{ [key: string]: string }>>,
  selectedOptInputCombined?: any,
): void => {
  const newFilterErrors = { ...filterErrors };

  // function to clear validation errors
  const deleteFilterErrors = (filterNames: string | string[]) => {
    if (Array.isArray(filterNames)) {
      filterNames.forEach((filterName) => {
        filter.inputProp?.inputCombinedOptions?.forEach((option) => {
          if (option.filterName === filterName) {
            delete newFilterErrors[option.filterName];
          }
        });
        delete newFilterErrors[filterName];
      });
    } else {
      filter.inputProp?.inputCombinedOptions?.forEach((option) => {
        if (option.filterName === filterNames) {
          delete newFilterErrors[option.filterName];
        }
      });
      delete newFilterErrors[filterNames];
    }
  };

  // Remove any validation errors for the currently updated filter
  if (filter?.inputProp?.enableDateRange) {
    const rangeFilterName = filter.inputProp.rangeFilterName;
    if (rangeFilterName) {
      delete newFilterErrors[rangeFilterName.from];
      delete newFilterErrors[rangeFilterName.to];
    }
    deleteFilterErrors(filter.filterName);
  }

  if (filter?.inputProp?.dependentFilters) {
    filter.inputProp.dependentFilters.forEach((dependentFilterId) => {
      const dependentFilter = filters.find((f) => f.idFilter === dependentFilterId);
      if (dependentFilter) {
        dependentFilter.inputProp?.inputCombinedOptions?.forEach((option) => {
          deleteFilterErrors(option.filterName);
        });
        deleteFilterErrors(dependentFilter.filterName);
      }
    });
  }

  let value = valore;
  if (value && typeof value === 'object' && 'value' in value) {
    value = value.value;
  }

  let validationRules = filter.validation;

  if (
    filter.inputProp?.dependentOn?.some((id) => {
      const dependentFilter = filters.find((el) => el.idFilter === id);
      return dependentFilter && !filterValues.some((fv) => fv.idFilter === dependentFilter.idFilter);
    })
  ) {
    validationRules = filter.validation?.validationDependent;
  }

  if (!validationRules) {
    setFilterErrors(newFilterErrors);
    return;
  }

  if (filter.inputProp?.isInputCombined && filter.inputProp.inputCombinedOptions) {
    filter.inputProp.inputCombinedOptions.forEach((option) => {
      delete newFilterErrors[option.filterName];
    });

    const selectedOption = filter.inputProp.inputCombinedOptions.find(
      (option) => option.value === selectedOptInputCombined?.value,
    );

    if (selectedOption) {
      let combinedValue = valore;

      if (selectedOption.isTextArea && Array.isArray(valore)) {
        combinedValue = valore.join('');
      }

      if (selectedOption.isRange && Array.isArray(combinedValue)) {
        combinedValue.forEach((rangeValue) => {
          if (
            validationRules &&
            validationRules.required &&
            (rangeValue === null || rangeValue === undefined || rangeValue === '')
          ) {
            newFilterErrors[selectedOption.filterName] = validationRules.message || '';
          } else if (validationRules && validationRules.type) {
            const type = validationRules.type;
            const isValidType = typeof rangeValue === type;
            if (!isValidType) {
              newFilterErrors[selectedOption.filterName] = `Invalid type. Expected ${validationRules.type}`;
            }

            if (validationRules.when && rangeValue !== null && rangeValue !== undefined && rangeValue !== '') {
              const when = validationRules.when;
              const regex = new RegExp(when[when.is === selectedOption.value ? 'then' : 'otherwise'].regex);
              if (!regex.test(rangeValue)) {
                newFilterErrors[selectedOption.filterName] =
                  when[when.is === selectedOption.value ? 'then' : 'otherwise'].message;
              }
            }
          }
        });
      }

      if (
        !selectedOption.isRange &&
        !selectedOption.isTextArea &&
        validationRules.required &&
        (combinedValue === null || combinedValue === undefined || combinedValue === '')
      ) {
        newFilterErrors[selectedOption.filterName] = validationRules.message || '';
      } else if (!selectedOption.isRange && validationRules.type) {
        const type = validationRules.type;
        const isValidType = typeof combinedValue === type;
        if (!isValidType) {
          newFilterErrors[selectedOption.filterName] = `Invalid type. Expected ${validationRules.type}`;
        }

        if (validationRules.when && combinedValue !== null && combinedValue !== undefined && combinedValue !== '') {
          const when = validationRules.when;
          const regex = new RegExp(when[when.is === selectedOption.value ? 'then' : 'otherwise'].regex);
          if (!regex.test(combinedValue)) {
            newFilterErrors[selectedOption.filterName] =
              when[when.is === selectedOption.value ? 'then' : 'otherwise'].message;
          }
        }
      }
    }
  } else {
    if (filter.inputType === 'multiselect') {
      if (validationRules.required && (!Array.isArray(value) || value.length === 0)) {
        newFilterErrors[filter.filterName[0]] = validationRules.message || '';
      }
    } else if (filter.inputType === 'date') {
      if (validationRules.required && filter.inputProp?.enableDateRange) {
        const rangeFilterName = filter.inputProp.rangeFilterName;
        const fromValue = filterValues
          .find((fv) => fv.options.some((o) => o.filterName === rangeFilterName?.from))
          ?.options.find((o) => o.filterName === rangeFilterName?.from)?.value;
        const toValue = filterValues
          .find((fv) => fv.options.some((o) => o.filterName === rangeFilterName?.to))
          ?.options.find((o) => o.filterName === rangeFilterName?.to)?.value;

        const filterValue = filterValues
          .find((fv) => fv.idFilter === filter.idFilter)
          ?.options.find((o) => o.filterName === filter.filterName[0])?.value;
        if (!filterValue && !fromValue && !toValue) {
          newFilterErrors[filter.filterName[0]] = validationRules.message || '';
        }
      } else {
        if (validationRules.required && (value === null || value === undefined || value === '')) {
          newFilterErrors[filter.filterName[0]] = validationRules.message || '';
        } else if (validationRules.type) {
          const type = validationRules.type;
          let isValidType;

          if (type === 'date') {
            isValidType = value instanceof Date;
          } else {
            isValidType = typeof value === type;
          }

          if (!isValidType) {
            newFilterErrors[filter.filterName[0]] = `Invalid type. Expected ${validationRules.type}`;
          }
        }
      }
    } else {
      if (validationRules.required && (value === null || value === undefined || value === '')) {
        newFilterErrors[filter.filterName[0]] = validationRules.message || '';
      } else if (validationRules.type) {
        const type = validationRules.type;
        let isValidType;

        if (type === 'date') {
          isValidType = value instanceof Date;
        } else {
          isValidType = typeof value === type;
        }

        if (!isValidType) {
          newFilterErrors[filter.filterName[0]] = `Invalid type. Expected ${validationRules.type}`;
        }
      }
    }
  }

  if (value === null || value === undefined || value === '') {
    deleteFilterErrors(filter.filterName);
  }

  setFilterErrors(newFilterErrors);
};

export const handleMultiSelectInputChange = (
  filter: IFilterDinamicForm,
  value: IOption[],
  filterValues: IFilterValue[],
  setFilterValues: React.Dispatch<React.SetStateAction<IFilterValue[]>>,
  filterErrors: { [key: string]: string },
  filters: IFilterDinamicForm[],
  setFilterErrors: React.Dispatch<React.SetStateAction<{ [key: string]: string }>>,
) => {
  let newFilterValues: IFilterValue[] = [...filterValues];

  // Find the dependent filters to remove
  const dependentFiltersToRemove = filters.filter(
    (depFilter) => depFilter.inputProp?.visibleCondition?.dependentOn === filter.idFilter,
  );

  // If value is an empty array, remove the entry with the matching idFilter
  if (value.length === 0) {
    if (dependentFiltersToRemove.length > 0) {
      dependentFiltersToRemove.forEach((depFilter) => {
        const filterValueIndex = newFilterValues.findIndex((fv) => fv.idFilter === depFilter.idFilter);
        const dependentValueArray = depFilter.inputProp?.visibleCondition?.value;

        // Remove the filter if its value is no longer present
        if (
          filterValueIndex !== -1 &&
          !dependentValueArray?.some((val) =>
            newFilterValues[filterValueIndex].options.some((option) => option[depFilter.filterName[0]] === val),
          )
        ) {
          newFilterValues.splice(filterValueIndex, 1);
        }
      });
    }

    newFilterValues = newFilterValues.filter((fv) => fv.idFilter !== filter.idFilter);
    setFilterValues(newFilterValues);
    validateFilter(filter, value, filterErrors, filters, newFilterValues, setFilterErrors);
    return;
  }

  // Find or initialize the current filter entry
  const currentFilterIndex = newFilterValues.findIndex((fv) => fv.idFilter === filter.idFilter);
  const currentFilter =
    currentFilterIndex !== -1 ? newFilterValues[currentFilterIndex] : { idFilter: filter.idFilter, options: [] };

  // Start with an empty options array to handle addition and removal of options
  let updatedOptions: { [key: string]: any }[] = [];

  if (filter.filterName.length > 1) {
    value.forEach((val: any) => {
      const optionValues = val && typeof val === 'object' && 'value' in val ? val.value : val;
      const optionArray = Array.isArray(optionValues) ? optionValues : [optionValues];

      const newOption: { [key: string]: any } = {};
      filter.filterName.forEach((name, index) => {
        if (optionArray[index] !== undefined) {
          if (name.includes('In')) {
            if (!newOption[name]) {
              newOption[name] = [];
            }
            newOption[name].push(optionArray[index]);
          } else {
            newOption[name] = optionArray[index];
          }
        }
      });

      updatedOptions.push(newOption);
    });
  } else {
    // Handle the case where filterName has length 1 and contains 'In'
    const filterKey = filter.filterName[0];
    if (filterKey.includes('In')) {
      const combinedOption: { [key: string]: any[] } = { [filterKey]: [] };

      // Collect all selected values in the combinedOption array
      value.forEach((val: any) => {
        const optionValue = val && typeof val === 'object' && 'value' in val ? val.value : val;
        combinedOption[filterKey].push(optionValue);
      });

      // Set the combined option as the only option for this filter
      updatedOptions = [combinedOption];
    } else {
      value.forEach((val: any) => {
        const optionValue = val && typeof val === 'object' && 'value' in val ? val.value : val;
        updatedOptions.push({ [filterKey]: optionValue });
      });
    }
  }

  // Set the updated options to the current filter
  currentFilter.options = updatedOptions;

  // Check if there are dependent filters to remove
  if (dependentFiltersToRemove.length > 0) {
    dependentFiltersToRemove.forEach((depFilter) => {
      const filterValueIndex = newFilterValues.findIndex((fv) => fv.idFilter === depFilter.idFilter);
      const dependentValueArray = depFilter.inputProp?.visibleCondition?.value;

      // Remove the filter if its value is no longer present
      if (
        filterValueIndex !== -1 &&
        !dependentValueArray?.some((value) =>
          newFilterValues[filterValueIndex].options.some((option) => option[depFilter.filterName[0]] === value),
        )
      ) {
        newFilterValues.splice(filterValueIndex, 1);
      }
    });
  }

  // Update the filter values array
  if (currentFilterIndex === -1) {
    newFilterValues.push(currentFilter);
  } else {
    newFilterValues[currentFilterIndex] = currentFilter;
  }

  setFilterValues(newFilterValues);
  validateFilter(filter, value, filterErrors, filters, newFilterValues, setFilterErrors);
};

export const handleTextInputChange = (
  filter: IFilterDinamicForm,
  value: any,
  filterValues: IFilterValue[],
  setFilterValues: React.Dispatch<React.SetStateAction<IFilterValue[]>>,
  filterErrors: { [key: string]: string },
  filters: IFilterDinamicForm[],
  setFilterErrors: React.Dispatch<React.SetStateAction<{ [key: string]: string }>>,
) => {
  const newFilterValues: IFilterValue[] = [...filterValues];

  const isEmptyValue = value === '' || value === null || value === undefined;

  // Extract the value directly
  const extractedValue = value;

  // Find the current filter
  const currentFilterIndex = newFilterValues.findIndex((fv) => fv.idFilter === filter.idFilter);
  const currentFilter =
    currentFilterIndex !== -1 ? newFilterValues[currentFilterIndex] : { idFilter: filter.idFilter, options: [] };

  if (isEmptyValue) {
    // If the value is empty, remove the filter if it exists
    if (currentFilterIndex !== -1) {
      newFilterValues.splice(currentFilterIndex, 1);
    }
  }
  // Otherwise, update or add the filter as usual
  else {
    // Determine the filter name to use
    const filterNameToUse = filter.inputProp?.filterNameDependent
      ? filter.inputProp.filterNameDependent
      : filter.filterName[0];

    const updatedOptions = [...currentFilter.options];
    const index = updatedOptions.findIndex((option: any) => option[filterNameToUse] !== undefined);

    if (index !== -1) {
      updatedOptions[index] = { [filterNameToUse]: extractedValue };
    } else {
      updatedOptions.push({ [filterNameToUse]: extractedValue });
    }

    currentFilter.options = updatedOptions;

    if (currentFilterIndex === -1) {
      newFilterValues.push(currentFilter);
    } else {
      newFilterValues[currentFilterIndex] = currentFilter;
    }
  }

  setFilterValues(newFilterValues);
  validateFilter(filter, extractedValue, filterErrors, filters, filterValues, setFilterErrors);
};

export const handleSelectInputChange = (
  filter: IFilterDinamicForm,
  value: any,
  filterValues: IFilterValue[],
  setFilterValues: React.Dispatch<React.SetStateAction<IFilterValue[]>>,
  filterErrors: { [key: string]: string },
  filters: IFilterDinamicForm[],
  setFilterErrors: React.Dispatch<React.SetStateAction<{ [key: string]: string }>>,
) => {
  let newFilterValues: IFilterValue[] = [...filterValues];
  const extractedValue = value && typeof value === 'object' && 'value' in value ? value.value : value;

  // Find the index of the current filter
  const currentFilterIndex = newFilterValues.findIndex((fv) => fv.idFilter === filter.idFilter);

  // Remove dependent filters if they exist
  if (filter.inputProp?.dependentFilters) {
    const dependentFilterIds = filter.inputProp.dependentFilters;

    // Remove dependent filters from the filter list
    newFilterValues = newFilterValues.filter((fv) => !dependentFilterIds.includes(fv.idFilter));
  }

  // Remove the filters that depend on the current filter (based on visibleCondition.dependentOn)
  newFilterValues = newFilterValues.filter((fv) => {
    // Check if the current filter is dependent on another filter.
    const filterDependency = filters.find((f) => f.inputProp?.visibleCondition?.dependentOn === filter.idFilter);

    // If fv.idFilter is equal to filterDependency, remove it.
    return filterDependency?.idFilter !== fv.idFilter;
  });

  // If the extracted value is null or undefined, remove the current filter
  if (extractedValue === null || extractedValue === undefined || extractedValue === '') {
    if (currentFilterIndex !== -1) {
      newFilterValues.splice(currentFilterIndex, 1);
    }
  } else {
    // Handle the update of options
    const currentFilter =
      currentFilterIndex !== -1 ? newFilterValues[currentFilterIndex] : { idFilter: filter.idFilter, options: [] };
    const updatedOptions = [...currentFilter.options];
    const index = updatedOptions.findIndex((option: any) => option[filter.filterName[0]] !== undefined);

    if (index !== -1) {
      updatedOptions[index] = { [filter.filterName[0]]: extractedValue };
    } else {
      updatedOptions.push({ [filter.filterName[0]]: extractedValue });
    }

    currentFilter.options = updatedOptions;

    if (currentFilterIndex === -1) {
      newFilterValues.push(currentFilter);
    } else {
      newFilterValues[currentFilterIndex] = currentFilter;
    }
  }

  // Set the new filter values
  setFilterValues(newFilterValues);
  validateFilter(filter, extractedValue, filterErrors, filters, filterValues, setFilterErrors);
};

export const getOptions = (
  filter: IFilterDinamicForm,
  idLoggedUser: number,
  filters: IFilterDinamicForm[],
  filterValues: IFilterValue[],
  t: (label: string) => string,
): IOption[] => {
  const dependentOn = filter.inputProp?.dependentOn;
  let options: IOption[] = [];

  if (dependentOn && dependentOn.length > 0) {
    let currentOptions: IOption[] | { [key: string]: IOption[] } | undefined =
      filter.inputProp?.options?.[idLoggedUser];

    if (!currentOptions) {
      return options;
    }

    // Iterate through dependent filters to update options
    dependentOn.forEach((dependentId, index) => {
      const filterName = filters.find((f) => f.idFilter === dependentId)?.filterName?.[0];

      if (filterName) {
        const filterValueObject = filterValues.find((fv) => fv.idFilter === dependentId);

        // Check if there is an object in `filterValueObject.options` with a key corresponding to `filterName`
        if (filterValueObject && Array.isArray(filterValueObject.options)) {
          const dependentOption = filterValueObject.options.find((option) => option.hasOwnProperty(filterName));

          if (dependentOption) {
            const dependentValue = dependentOption[filterName];

            if (typeof currentOptions === 'object' && currentOptions !== null) {
              const valueKey =
                typeof dependentValue === 'object' && dependentValue !== null && 'value' in dependentValue
                  ? dependentValue.value
                  : dependentValue;

              currentOptions = (currentOptions as { [key: string]: IOption[] })[valueKey];
            } else {
              currentOptions = [];
            }

            if (index === dependentOn.length - 1) {
              if (Array.isArray(currentOptions)) {
                options = currentOptions;
              } else if (typeof currentOptions === 'object' && currentOptions !== null) {
                const nestedOptions = Object.values(currentOptions).flat() as IOption[];
                options = options.concat(nestedOptions);
              }
            }
          } else {
            currentOptions = [];
          }
        } else {
          currentOptions = [];
        }
      } else {
        currentOptions = [];
      }
    });
  } else {
    const directUserOptions = filter.inputProp?.options;
    if (Array.isArray(directUserOptions)) {
      options = directUserOptions;
    } else if (typeof directUserOptions === 'object' && directUserOptions !== null) {
      options = Object.values(directUserOptions).flat();
    }
  }

  if (filter.inputProp?.translate) {
    return options.map((option) => ({
      value: option.value,
      label: t(option.label),
    }));
  }

  return options;
};

export const getInputValue = (filter: IFilterDinamicForm, filterValues: IFilterValue[]) => {
  // Find the object in filterValues that corresponds to the idFilter of the current filter
  const foundFilter = filterValues.find((fv) => fv.idFilter === filter.idFilter);

  if (foundFilter) {
    // Use filterNameDependent if it exists; otherwise, use filter.filterName
    const filterNameToUse = filter.inputProp?.filterNameDependent
      ? [filter.inputProp.filterNameDependent]
      : filter.filterName;

    // Find the option that contains all keys from filterNameToUse
    const matchingOption = foundFilter.options.find((opt) => filterNameToUse.every((name) => opt.hasOwnProperty(name)));

    if (matchingOption) {
      // Extract and return the corresponding values
      const value = filterNameToUse.map((name) => matchingOption[name]);

      // If there is only one value, return it directly
      if (value.length === 1) {
        return value[0];
      }

      // If there are multiple values, return them as an array
      return value;
    }
  }

  // If nothing is found, return an empty string
  return '';
};

export const getSelectedOption = (
  filter: IFilterDinamicForm,
  options: IOption[],
  filterValues: IFilterValue[],
): IOption | null => {
  // Find the filterValue object corresponding to the idFilter
  const foundFilter = filterValues.find((fv) => fv.idFilter === filter.idFilter);

  if (foundFilter) {
    // Determine the filter name to use as the key for the option
    const keyToSearch = filter.filterName[0];

    // Get the selected value for the specific key
    const selectedValue = foundFilter.options.find((option) => option[keyToSearch] !== undefined)?.[keyToSearch];

    // Find the option corresponding to the selected value
    const selectedOption = options.find((option) => {
      // Compare the option value with the selected value
      return option.value === selectedValue;
    });

    return selectedOption || null;
  }

  return null;
};

export const getSelectedOptionsMultiSelect = (
  filter: IFilterDinamicForm,
  options: IOption[],
  filterValues: IFilterValue[],
): IOption[] => {
  // Find the filter that matches the idFilter
  const filterValueEntry = filterValues.find((fv) => fv.idFilter === filter.idFilter);

  if (!filterValueEntry) {
    return [];
  }

  // If `filterName.length` is 1 and contains 'In', group the values into the same array
  let selectedOptions: IOption[];
  if (filter.filterName.length === 1 && filter.filterName[0].includes('In')) {
    const filterName = filter.filterName[0];
    const combinedValues = filterValueEntry.options.reduce((acc: any[], filterEntry: any) => {
      if (filterEntry[filterName]) {
        acc.push(...filterEntry[filterName]);
      }
      return acc;
    }, []);

    // Find the options corresponding to the combined values
    selectedOptions = options.filter((option) => {
      return combinedValues.some((val) =>
        Array.isArray(option.value) ? option.value.includes(val) : option.value === val,
      );
    });
  } else {
    // Function to check if an option matches the filter entry
    const optionMatchesFilter = (option: IOption, filterEntry: any): boolean => {
      return filter.filterName.every((filterName, index) => {
        const filterEntryValue = filterEntry[filterName];
        const optionValue = Array.isArray(option.value) ? option.value[index] : option.value;

        if (Array.isArray(filterEntryValue)) {
          // If the filter value is an array, check if optionValue is included in filterEntryValue
          return filterEntryValue.includes(optionValue);
        } else {
          // If the filter value is a single value, compare it with the option value
          return optionValue === filterEntryValue;
        }
      });
    };

    // Map each filter entry to its corresponding option
    selectedOptions = filterValueEntry.options
      .map((filterEntry) => {
        return options.find((option) => optionMatchesFilter(option, filterEntry)) || null;
      })
      .filter((opt): opt is IOption => opt !== null);
  }

  return selectedOptions;
};

export const getDateValue = (filter: IFilterDinamicForm, filterValues: IFilterValue[]) => {
  const foundFilter = filterValues.find((fv) => fv.idFilter === filter.idFilter);

  if (filter.inputType === 'date') {
    if (foundFilter) {
      const filterName = filter.filterName[0];
      if (typeof filterName === 'string') {
        const dynamicOption = foundFilter.options.find((option) => filterName in option);

        if (dynamicOption !== undefined) {
          const dynamicValue = dynamicOption[filterName];
          return typeof dynamicValue === 'string' ? utils.date.convertToObjectDate(dynamicValue) : dynamicValue;
        }
      }
    }

    if (filter.inputProp?.enableDateRange && filter.inputProp.rangeFilterName) {
      const { from: fromName, to: toName } = filter.inputProp.rangeFilterName;

      const fromFilterValue = filterValues.find((fv) => fv.options.some((option) => fromName in option));
      const toFilterValue = filterValues.find((fv) => fv.options.some((option) => toName in option));

      const fromOption = fromFilterValue?.options.find((option) => fromName in option);
      const toOption = toFilterValue?.options.find((option) => toName in option);

      const fromValue = fromOption ? fromOption[fromName] : null;
      const toValue = toOption ? toOption[toName] : null;

      return {
        from: fromValue
          ? typeof fromValue === 'string'
            ? utils.date.convertToObjectDate(fromValue)
            : fromValue
          : null,
        to: toValue ? (typeof toValue === 'string' ? utils.date.convertToObjectDate(toValue) : toValue) : null,
      };
    }
  }
  return null;
};

export const handleDateChange = (
  filter: IFilterDinamicForm,
  filterName: string,
  date: any,
  filterValues: IFilterValue[],
  setFilterValues: React.Dispatch<React.SetStateAction<IFilterValue[]>>,
  filterErrors: { [key: string]: string },
  filters: IFilterDinamicForm[],
  setFilterErrors: React.Dispatch<React.SetStateAction<{ [key: string]: string }>>,
) => {
  // Find the current filter object in the filter values
  const currentFilterIndex = filterValues.findIndex((fv) => fv.idFilter === filter.idFilter);
  const currentFilter =
    currentFilterIndex !== -1 ? filterValues[currentFilterIndex] : { idFilter: filter.idFilter, options: [] };

  // Handle the case where the date is null
  if (date === null) {
    // Remove the filter if the value is null
    if (currentFilterIndex !== -1) {
      const newFilterValues = filterValues.filter((fv) => fv.idFilter !== filter.idFilter);
      setFilterValues(newFilterValues);
    }
  } else {
    // Handle updating the value
    const updatedOptions = [...currentFilter.options];

    // Update the filter value using filterName
    const existingIndex = updatedOptions.findIndex((option) => filterName in option);

    if (existingIndex !== -1) {
      // If an option with the key filterName already exists, update it
      updatedOptions[existingIndex] = { [filterName]: date };
    } else {
      // Otherwise, add a new option
      updatedOptions.push({ [filterName]: date });
    }

    // Update the filter values
    const newFilterValues =
      currentFilterIndex !== -1
        ? filterValues.map((fv, index) => (index === currentFilterIndex ? { ...fv, options: updatedOptions } : fv))
        : [...filterValues, { idFilter: filter.idFilter, options: updatedOptions }];

    setFilterValues(newFilterValues);
  }

  // Validate the filter if necessary
  validateFilter(filter, date, filterErrors, filters, filterValues, setFilterErrors);
};
